import { MODEL_LABELS, calculateSourceGeometry, isFiniteNumber, isPositive, targetCenters } from './physics'
import { csvCell, parseCsv } from './io'
import type { AppState, CalibrationDataset, CalibrationFit } from './types'

export const CALIBRATION_ESTIMATOR = 'primary-lobe-half-max-centroid-v1' as const

function allBlankCalibrationRecordLines(text: string): number[] {
  return text.split('\n').flatMap((rawRecord, index) => {
    if (index === 0) return []
    const record = rawRecord.replace(/\r$/, '')
    if (!record.includes(',')) return []
    return record.split(',').every((cell) => cell.trim() === '') ? [index + 1] : []
  })
}

export function parseCalibrationCsv(text: string, name = 'Imported measurement'): {
  dataset: CalibrationDataset | null
  errors: string[]
} {
  let rows: string[][]
  try {
    rows = parseCsv(text)
  } catch (error) {
    return { dataset: null, errors: [error instanceof Error ? error.message : String(error)] }
  }
  const errors: string[] = []
  if (!rows.length) return { dataset: null, errors: ['Calibration CSV is empty.'] }
  const headers = rows[0].map((value) => value.trim())
  const xIndex = headers.indexOf('x_mm')
  const viewColumns = headers
    .map((header, index) => ({ header, index }))
    .filter(({ header }) => /^view_\d+$/i.test(header))
  if (xIndex < 0) errors.push('Calibration CSV must include x_mm.')
  if (!viewColumns.length) errors.push('Calibration CSV must include at least one view_N column.')
  if (errors.length) return { dataset: null, errors }
  allBlankCalibrationRecordLines(text).forEach((line) => {
    errors.push(`Row ${line}: x_mm must not be blank.`)
    viewColumns.forEach(({ header }) => errors.push(`Row ${line}: ${header} must not be blank.`))
  })
  if (errors.length) return { dataset: null, errors }
  if (rows.length < 4) {
    return { dataset: null, errors: ['Calibration CSV needs a header and at least three samples.'] }
  }

  const xMm: number[] = []
  const intensities = viewColumns.map(() => [] as number[])
  rows.slice(1).forEach((row, rowIndex) => {
    const line = rowIndex + 2
    if (row.length !== headers.length) {
      errors.push(`Row ${line}: expected ${headers.length} fields, found ${row.length}.`)
      return
    }
    const xCell = row[xIndex].trim()
    const x = xCell === '' ? Number.NaN : Number(xCell)
    if (xCell === '') errors.push(`Row ${line}: x_mm must not be blank.`)
    else if (!Number.isFinite(x)) errors.push(`Row ${line}: x_mm must be finite.`)
    xMm.push(x)
    viewColumns.forEach(({ index }, viewIndex) => {
      const cell = row[index].trim()
      const value = cell === '' ? Number.NaN : Number(cell)
      if (cell === '') errors.push(`Row ${line}: ${headers[index]} must not be blank.`)
      else if (!Number.isFinite(value)) errors.push(`Row ${line}: ${headers[index]} must be finite.`)
      else if (value < 0) errors.push(`Row ${line}: ${headers[index]} must be nonnegative.`)
      intensities[viewIndex].push(value)
    })
  })
  for (let index = 1; index < xMm.length; index += 1) {
    if (xMm[index] <= xMm[index - 1]) {
      errors.push('x_mm values must be strictly increasing with no duplicates.')
      break
    }
  }
  if (errors.length) return { dataset: null, errors }
  return {
    dataset: {
      name,
      xMm,
      viewNames: viewColumns.map(({ header }) => header),
      intensities,
    },
    errors: [],
  }
}

interface PrimaryLobeEstimate {
  centerMm: number | null
  fwhmMm: number | null
  error: string | null
}

function interpolateCrossing(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  level: number,
): number | null {
  const delta = y1 - y0
  if (Math.abs(delta) <= 1e-15) return null
  const fraction = (level - y0) / delta
  if (fraction < 0 || fraction > 1) return null
  return x0 + fraction * (x1 - x0)
}

function primaryLobeEstimate(x: number[], values: number[]): PrimaryLobeEstimate {
  const invalid = (error: string): PrimaryLobeEstimate => ({ centerMm: null, fwhmMm: null, error })
  if (x.length !== values.length) {
    return invalid('sample count does not match x_mm; provide one intensity value per position.')
  }
  if (x.length < 3) {
    return invalid('is under-resolved; provide at least three samples across the primary lobe.')
  }
  if (
    x.some((value, index) => !Number.isFinite(value) || (index > 0 && value <= x[index - 1])) ||
    values.some((value) => !Number.isFinite(value))
  ) {
    return invalid('contains invalid samples; use finite intensities and strictly increasing x_mm positions.')
  }
  const baseline = Math.min(...values)
  const corrected = values.map((value) => Math.max(0, value - baseline))
  const peak = Math.max(...corrected)
  if (peak <= 1e-15) {
    return invalid('has no measurable primary lobe above its baseline; increase signal or check the column.')
  }
  const peakIndex = corrected.indexOf(peak)
  const halfPeak = peak / 2
  const peakTieTolerance = Math.max(1e-12, peak * 1e-9)
  let dominantPeakRegions = 0
  for (let index = 0; index < corrected.length; index += 1) {
    if (corrected[index] < peak - peakTieTolerance) continue
    dominantPeakRegions += 1
    while (
      index + 1 < corrected.length &&
      corrected[index + 1] >= peak - peakTieTolerance
    ) {
      index += 1
    }
  }
  if (dominantPeakRegions > 1) {
    return invalid('has tied dominant peaks; isolate one primary lobe or increase spatial resolution.')
  }
  const components: Array<{ left: number; right: number }> = []
  for (let index = 0; index < corrected.length; index += 1) {
    if (corrected[index] < halfPeak) continue
    const left = index
    while (index + 1 < corrected.length && corrected[index + 1] >= halfPeak) index += 1
    components.push({ left, right: index })
  }
  const primary = components.find(({ left, right }) => peakIndex >= left && peakIndex <= right)
  if (!primary) {
    return invalid('has no resolvable half-maximum component; increase sampling density.')
  }
  if (components.length !== 1) {
    return invalid('has competing lobes at half maximum; isolate one primary lobe or correct the view column.')
  }
  if (primary.left === 0 || primary.right === corrected.length - 1) {
    return invalid('primary lobe is clipped by the measurement boundary; extend the x_mm range.')
  }
  if (primary.right - primary.left + 1 < 3) {
    return invalid('primary lobe is under-resolved at half maximum; decrease the x_mm sample spacing.')
  }
  const leftCrossing = interpolateCrossing(
    x[primary.left - 1],
    corrected[primary.left - 1],
    x[primary.left],
    corrected[primary.left],
    halfPeak,
  )
  const rightCrossing = interpolateCrossing(
    x[primary.right],
    corrected[primary.right],
    x[primary.right + 1],
    corrected[primary.right + 1],
    halfPeak,
  )
  if (leftCrossing === null || rightCrossing === null || rightCrossing <= leftCrossing) {
    return invalid('half-maximum crossings are unresolved; decrease the x_mm sample spacing.')
  }
  const localX = [leftCrossing, ...x.slice(primary.left, primary.right + 1), rightCrossing]
  const localValues = [
    halfPeak,
    ...corrected.slice(primary.left, primary.right + 1),
    halfPeak,
  ]
  let area = 0
  let firstMoment = 0
  for (let index = 0; index < localX.length - 1; index += 1) {
    const dx = localX[index + 1] - localX[index]
    area += ((localValues[index] + localValues[index + 1]) * dx) / 2
    firstMoment +=
      ((localX[index] * localValues[index] + localX[index + 1] * localValues[index + 1]) * dx) /
      2
  }
  if (area <= 1e-15) {
    return invalid('has negligible integrated primary-lobe signal; increase signal or sampling density.')
  }
  return {
    centerMm: firstMoment / area,
    fwhmMm: rightCrossing - leftCrossing,
    error: null,
  }
}

function linearFit(x: number[], y: number[]): { slope: number; intercept: number; rms: number } | null {
  if (x.length !== y.length || x.length < 2) return null
  const meanX = x.reduce((sum, value) => sum + value, 0) / x.length
  const meanY = y.reduce((sum, value) => sum + value, 0) / y.length
  const variance = x.reduce((sum, value) => sum + (value - meanX) ** 2, 0)
  if (variance <= 1e-15) return null
  const covariance = x.reduce(
    (sum, value, index) => sum + (value - meanX) * (y[index] - meanY),
    0,
  )
  const slope = covariance / variance
  const intercept = meanY - slope * meanX
  const rms = Math.sqrt(
    y.reduce((sum, value, index) => sum + (value - (intercept + slope * x[index])) ** 2, 0) /
      y.length,
  )
  return { slope, intercept, rms }
}

function measuredCrosstalk(
  dataset: CalibrationDataset,
  state: AppState,
  centers: number[],
): number[] {
  if (!isPositive(state.view.pupilDiameterMm)) return centers.map(() => Number.NaN)
  const measuredCenters = dataset.intensities.map(
    (values) => primaryLobeEstimate(dataset.xMm, values).centerMm,
  )
  const sourceOrder = measuredCenters
    .map((center, index) => ({ center, index }))
    .filter((item): item is { center: number; index: number } => isFiniteNumber(item.center))
    .sort((a, b) => a.center - b.center)
  const targetOrder = centers.map((center, index) => ({ center, index })).sort((a, b) => a.center - b.center)
  const desired = Array(centers.length).fill(0)
  targetOrder.forEach((target, order) => {
    desired[target.index] = sourceOrder[order]?.index ?? centers.length - 1 - target.index
  })
  return centers.map((center, targetIndex) => {
    const low = center - state.view.pupilDiameterMm! / 2
    const high = center + state.view.pupilDiameterMm! / 2
    const transfers = dataset.intensities.map((values) =>
      integrateWindow(dataset.xMm, values, low, high),
    )
    if (transfers.some((value) => !Number.isFinite(value))) return Number.NaN
    const total = transfers.reduce((sum, value) => sum + value, 0)
    const intended = transfers[desired[targetIndex]] ?? 0
    return total > 1e-15 ? (total - intended) / total : Number.NaN
  })
}

function integrateWindow(x: number[], values: number[], low: number, high: number): number {
  if (x.length !== values.length || x.length < 2 || high <= low) return Number.NaN
  let total = 0
  for (let index = 0; index < x.length - 1; index += 1) {
    const x0 = x[index]
    const x1 = x[index + 1]
    const y0 = values[index]
    const y1 = values[index + 1]
    if (![x0, x1, y0, y1].every(Number.isFinite) || x1 <= x0) return Number.NaN
    const start = Math.max(low, x0)
    const end = Math.min(high, x1)
    if (end <= start) continue
    const slope = (y1 - y0) / (x1 - x0)
    const startValue = y0 + slope * (start - x0)
    const endValue = y0 + slope * (end - x0)
    total += ((startValue + endValue) * (end - start)) / 2
  }
  return total
}

export function fitCalibration(dataset: CalibrationDataset, state: AppState): CalibrationFit {
  const source = calculateSourceGeometry(state)
  const centerResult = targetCenters(state)
  const warnings: string[] = [
    'Fit uses validated primary-lobe half-maximum centroids and a first-order affine zone-center model.',
    'Measured intensities are preserved; fitted values do not overwrite design inputs.',
  ]
  const estimates = dataset.intensities.map((values) => primaryLobeEstimate(dataset.xMm, values))
  estimates.forEach((estimate, index) => {
    if (estimate.error) {
      warnings.push(`${dataset.viewNames[index] ?? `view_${index}`}: ${estimate.error}`)
    }
  })
  const measuredCentersMm = estimates.map((estimate) => estimate.centerMm)
  const measuredFwhmMm = estimates.map((estimate) => estimate.fwhmMm)
  const validCenters = measuredCentersMm.filter(isFiniteNumber)
  if (
    validCenters.length !== state.view.views ||
    dataset.viewNames.length !== state.view.views ||
    !isPositive(source.sourcePitchMm) ||
    !isPositive(state.view.targetDistanceMm)
  ) {
    warnings.push('Fitting requires one measured curve per configured view, source pitch, and viewing distance.')
    return {
      model: MODEL_LABELS.measured,
      estimator: CALIBRATION_ESTIMATOR,
      measuredCentersMm: measuredCentersMm.map((value) => value ?? Number.NaN),
      measuredFwhmMm,
      measuredCrosstalk: centerResult.missing.length
        ? []
        : measuredCrosstalk(dataset, state, centerResult.centers),
      fittedReducedGapMm: null,
      fittedZoneSpacingMm: null,
      fittedLensPhaseMm: null,
      centerFitSlope: null,
      centerFitIntercept: null,
      rmsCenterResidualMm: null,
      warnings,
    }
  }
  const sourceOffsets = Array.from(
    { length: state.view.views },
    (_, index) => (index - (state.view.views - 1) / 2) * source.sourcePitchMm!,
  )
  const fit = linearFit(sourceOffsets, validCenters)
  if (!fit || Math.abs(fit.slope) < 1e-12) {
    warnings.push('Zone-center slope is degenerate; no reduced-gap fit is reported.')
  } else if (fit.slope > 0) {
    warnings.push('Measured view order is not inverted relative to source order; physical fitted values are withheld. Verify column labels or coordinate sign.')
  }
  const physicalFit = fit && fit.slope < -1e-12 ? fit : null
  const fittedReducedGapMm = physicalFit ? -state.view.targetDistanceMm! / physicalFit.slope : null
  const fittedZoneSpacingMm = physicalFit ? -physicalFit.slope * source.sourcePitchMm! : null
  const fittedLensPhaseMm =
    physicalFit && fittedReducedGapMm
      ? physicalFit.intercept / (1 + state.view.targetDistanceMm! / fittedReducedGapMm)
      : null
  return {
    model: MODEL_LABELS.measured,
    estimator: CALIBRATION_ESTIMATOR,
    measuredCentersMm: measuredCentersMm.map((value) => value ?? Number.NaN),
    measuredFwhmMm,
    measuredCrosstalk: centerResult.missing.length
      ? []
      : measuredCrosstalk(dataset, state, centerResult.centers),
    fittedReducedGapMm,
    fittedZoneSpacingMm,
    fittedLensPhaseMm,
    centerFitSlope: fit?.slope ?? null,
    centerFitIntercept: fit?.intercept ?? null,
    rmsCenterResidualMm: fit?.rms ?? null,
    warnings,
  }
}

export function createSyntheticCalibrationDataset(state: AppState): CalibrationDataset {
  const centerResult = targetCenters(state)
  const fallback = Array.from(
    { length: state.view.views },
    (_, index) => (index - (state.view.views - 1) / 2) * 50,
  )
  const targets = centerResult.missing.length ? fallback : centerResult.centers
  const xMm = Array.from({ length: 301 }, (_, index) => -300 + index * 2)
  const sigma = 7
  const intensities = targets
    .slice()
    .reverse()
    .map((center, view) =>
      xMm.map((x) => {
        const main = Math.exp(-0.5 * ((x - center - 2.4) / sigma) ** 2)
        const leakage = targets.reduce((sum, other, otherView) => {
          if (otherView === targets.length - 1 - view) return sum
          return sum + 0.025 * Math.exp(-0.5 * ((x - other - 2.4) / (sigma * 1.2)) ** 2)
        }, 0)
        return 0.01 + main + leakage
      }),
    )
  return {
    name: 'Synthetic calibration example',
    xMm,
    viewNames: Array.from({ length: state.view.views }, (_, index) => `view_${index}`),
    intensities,
  }
}

export function exportCalibrationCsv(dataset: CalibrationDataset): string {
  const lines = [['x_mm', ...dataset.viewNames].map(csvCell).join(',')]
  dataset.xMm.forEach((x, row) => {
    lines.push([x, ...dataset.intensities.map((view) => view[row])].map(csvCell).join(','))
  })
  return `${lines.join('\n')}\n`
}
